import LayeredAutocompleteSelect from "../../components/LayeredAutocompleteSelect";
import React, { useId, useMemo } from "react";
import { Autocomplete, Box, Button, CircularProgress, MenuItem, Select, TextField, Typography } from "@mui/material";
import { NEUTRAL, RADIUS, VIOLET } from "../../styles/brandTokens";
import type { TheoOverviewFilterOptions } from "../../types/integration";
import { text, windowSelectSx } from "../monitor/SummaryPanel";
import { SUMMARY_WINDOWS } from "../theoAutomationsMonitorFilters";
import { OverviewFilters, hasActiveSlice } from "./theoOverviewModel";

const ALL = "";

interface CustomerOption {
  value: string;
  label: string;
}

interface OverviewFilterBarProps {
  filters: OverviewFilters;
  options: TheoOverviewFilterOptions | null;
  optionsFailed: boolean;
  /** Shown while options load, or when the URL names a customer not in the list. */
  customerName: string | null;
  loading: boolean;
  refreshedAt: string | null;
  onChange: (patch: Partial<OverviewFilters>) => void;
}

const Field: React.FC<{ label: string; labelId: string; children: React.ReactNode }> = ({ label, labelId, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography component="label" id={labelId} sx={text(12.5, NEUTRAL.textSecondary, 600)}>
      {label}
    </Typography>
    {children}
  </Box>
);

const OverviewFilterBar: React.FC<OverviewFilterBarProps> = ({
  filters,
  options,
  optionsFailed,
  customerName,
  loading,
  refreshedAt,
  onChange,
}) => {
  const windowLabelId = useId();
  const agentLabelId = useId();
  const customerLabelId = useId();

  const agentOptions = useMemo(() => {
    const listed = (options?.agents || []).map((agent) => ({
      value: agent.value,
      label: agent.deleted ? `${agent.label} (deleted)` : agent.label,
    }));
    // A slug from a shared link can name an agent this list hasn't loaded yet.
    if (filters.agent && !listed.some((agent) => agent.value === filters.agent)) {
      return [...listed, { value: filters.agent, label: filters.agent }];
    }
    return listed;
  }, [filters.agent, options?.agents]);

  const customerOptions: CustomerOption[] = options?.customers || [];
  const selectedCustomer: CustomerOption | null = filters.customer
    ? customerOptions.find((option) => option.value === filters.customer) || {
        value: filters.customer,
        label: customerName || "Selected customer",
      }
    : null;

  return (
    <Box
      component="section"
      aria-label="Filters"
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 2.5, rowGap: 1.25 }}
    >
      <Field label="Showing" labelId={windowLabelId}>
        <Select
          size="small"
          labelId={windowLabelId}
          value={filters.window}
          onChange={(event) => onChange({ window: event.target.value })}
          sx={windowSelectSx}
        >
          {SUMMARY_WINDOWS.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={text(13.5)}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Field>

      <Field label="Agent" labelId={agentLabelId}>
        <LayeredAutocompleteSelect
          label=""
          fullWidth={false}
          size="small"
          value={filters.agent}
          onChange={value => onChange({ agent: value })}
          sx={{ ...windowSelectSx, minWidth: 180 }}
          inputAriaLabel="Agent"
          disableClearable
          options={[{ value: ALL, label: "All agents" }, ...agentOptions]}
        />
      </Field>

      <Field label="Customer" labelId={customerLabelId}>
        <Autocomplete<CustomerOption>
          size="small"
          options={customerOptions}
          value={selectedCustomer}
          loading={!options}
          noOptionsText={optionsFailed ? "Couldn't load customers" : "No customers"}
          onChange={(_, option) => onChange({ customer: option?.value || ALL })}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          getOptionKey={(option) => option.value}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.value} sx={text(13.5)}>
              {option.label}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="All customers"
              inputProps={{ ...params.inputProps, "aria-labelledby": customerLabelId }}
            />
          )}
          sx={{
            width: 240,
            "& .MuiOutlinedInput-root": { ...windowSelectSx, py: "1px !important" },
            "& .MuiInputBase-input": text(13.5),
          }}
        />
      </Field>

      {hasActiveSlice(filters) ? (
        <Button
          size="small"
          onClick={() => onChange({ agent: ALL, customer: ALL })}
          sx={{
            ...text(13, NEUTRAL.textSecondary, 600),
            textTransform: "none",
            borderRadius: `${RADIUS.control}px`,
            "&:hover": { background: NEUTRAL.hover, color: VIOLET.main },
          }}
        >
          Clear filters
        </Button>
      ) : null}

      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
        {loading ? <CircularProgress size={14} sx={{ color: VIOLET.main }} aria-label="Loading" /> : null}
        {refreshedAt ? (
          <Typography sx={text(12, NEUTRAL.textMuted)}>
            Updated {new Date(refreshedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default OverviewFilterBar;
